import {Component, Inject, OnInit} from '@angular/core';
import {UtilsService} from "../../shared/utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ApiRequestService} from "../../shared/api-request.service";
import {AppService} from "../../app.service";
import {NgForm} from "@angular/forms";
import {AssetTypeModel} from "../../models/asset-type.model";

@Component({
  selector: 'app-asset-types-edit',
  templateUrl: './asset-types-edit.component.html',
  styleUrls: ['./asset-types-edit.component.scss']
})
export class AssetTypesEditComponent implements OnInit {

  inProgress: boolean = false;
  asset_type: AssetTypeModel = new AssetTypeModel();
  on_parent_list: boolean = true;

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<AssetTypesEditComponent>,
    private api: ApiRequestService,
    private app: AppService
  ) { }

  ngOnInit() {
    const asset_type_id = this.dialogData?.asset_type_id;
    const parent_id = this.dialogData?.parent_id;

    this.on_parent_list = this.dialogData?.on_parent_list ?? this.on_parent_list;

    if (typeof asset_type_id === 'number' && asset_type_id > 0) {
      this.find(asset_type_id);
    } else if (typeof parent_id === 'number' && parent_id > 0 && !this.on_parent_list) {
      this.asset_type.parent_id = parent_id;
    }
  }

  /**
   * Handles form submission by checking if the form is valid and whether the submit button has already been pressed.
   * If the form is invalid, a form validation error message is displayed.
   * Depending on the asset_type.id value, it either creates or updates the form data.
   *
   * @param {NgForm} form - The NgForm object representing the form to be submitted.
   * @param {boolean} [autoCloseDialog=true] - Flag indicating whether to automatically close the dialog after submission.
   * @return {void}
   */
  onSubmit(form: NgForm, autoCloseDialog: boolean = true): void {
    // Check if the button was already pressed.
    if ( this.inProgress ) {
      return;
    }

    // Check form validation.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    this.inProgress = true;

    !this.asset_type.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog);
  }

  /**
   * Finds a Asset Type by its ID.
   *
   * @param {number} id - The ID of the Asset Type.
   * @private
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/asset-types/${id}`)
      .then((response) => {
        this.asset_type = response.data as AssetTypeModel;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Displays a confirmation modal for deleting an Asset Type or Subtype and archives it upon user confirmation.
   *
   * @return {void}
   */
  onDelete(): void {
    this.utils.showModal(
      `Remove Asset ${this.asset_type?.parent ? 'Subtype' : 'Type'}`,
      `Are you sure you want to archive "${this.asset_type.name}"?`,
      () => {
        this.api.makeRequest('delete', `v2/asset-types/${this.asset_type.id}`)
          .then((response) => {
            this.utils.showToast(response?.message || `"${this.asset_type.name}" has been archived.`);
            this.dialogRef.close(true);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Updates the Asset Type or Subtype with new data.
   *
   * @param {boolean} autoCloseDialog - A boolean flag indicating whether to automatically close the dialog after update. Default is true.
   * @return void
   */
  private update(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('put', `v2/asset-types/${this.asset_type.id}`, this.asset_type)
      .then((response) => {
        this.asset_type = response.data as AssetTypeModel;
        this.onSuccess(`Updated: "${this.asset_type.name}"`, autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Creates a new Asset Type or Subtype.
   *
   * @param {boolean} autoCloseDialog - A boolean flag indicating whether to automatically close the dialog after creation. Default is true.
   * @return void
   */
  private create(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('post', 'v2/asset-types', this.asset_type)
      .then((response) => {
        this.asset_type = response.data as AssetTypeModel;
        this.onSuccess(`New Asset ${this.asset_type?.parent ? 'Subtype' : 'Type'} "${this.asset_type.name}" created.`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Handles the success message of the API request or custom.
   *
   * @param {string} message - The message to display in the toast.
   * @param {boolean} autoCloseDialog - Whether the dialog should be automatically closed. Default value is true.
   * @return {void}
   */
  private onSuccess(message: string, autoCloseDialog: boolean = true): void {
    this.utils.showToast(message);
    // Check if the dialog should be closed.
    if ( autoCloseDialog ) {
      this.dialogRef.close(this.asset_type);
    }
  }

  /**
   * Checks whether the Asset Type is a Subtype based on the presence of parent and id.
   *
   * @returns {boolean} Returns true if the asset type is a subtype based on the conditions, otherwise false.
   */
  isSubtype(): boolean {
    // Editing existing subtype
    if (this.asset_type?.id && this.asset_type?.parent) {
      return true;
    }

    // Creating from Subtypes list
    return !this.asset_type?.id && !this.on_parent_list;
  }

  /**
   * Returns a label for the Asset Type based on whether it is a Subtype or a Type.
   *
   * @return {string} The asset type label ('Asset Subtype' if it is a subtype, 'Asset Type' if it is a type)
   */
  assetTypeLabel(): string {
    return 'Asset ' + (this.isSubtype() ? 'Subtype' : 'Type');
  }

}
